import { useState, type ReactElement } from 'react';
import { HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

/**
 * Package managers whose install commands Agents Activity recognizes (KIR-3483).
 * Grouped by ecosystem purely for readability. Mirrors the command classifier
 * in the backend `parse-command` handler — keep in sync when a manager is added.
 */
const TRACKED_MANAGERS: ReadonlyArray<{ ecosystem: string; managers: string }> =
  [
    { ecosystem: 'JavaScript', managers: 'npm, yarn, pnpm' },
    { ecosystem: 'Python', managers: 'pip, uv, poetry, pipenv' },
    { ecosystem: 'Ruby', managers: 'gem, bundler' },
    { ecosystem: 'Go', managers: 'go modules' },
    { ecosystem: 'Rust', managers: 'cargo' },
    { ecosystem: 'PHP', managers: 'composer' },
    { ecosystem: 'Java', managers: 'maven' },
    { ecosystem: '.NET', managers: 'dotnet (NuGet)' },
  ];

/**
 * Git commands the Repositories tab records. Mirrors `classifyGit` in the
 * backend `parse-command` handler — keep in sync (KIR-4848).
 */
const TRACKED_GIT_ACTIONS: ReadonlyArray<{ action: string; command: string }> =
  [
    { action: 'clone', command: 'git clone' },
    { action: 'pull', command: 'git pull' },
    { action: 'fetch', command: 'git fetch' },
    { action: 'push', command: 'git push' },
  ];

export type AgentsActivityTabValue = 'packages' | 'repositories';

interface WhatIsTrackedDialogProps {
  /** The active Agents Activity tab; picks which help text the dialog shows. */
  tab?: AgentsActivityTabValue;
}

/**
 * "What is tracked?" help button + dialog for Agents Activity (KIR-3483).
 * UI-only: a short explanation of what the active tab records, with the
 * supported package managers or git actions (KIR-4848).
 */
export function WhatIsTrackedDialog({
  tab = 'packages',
}: WhatIsTrackedDialogProps): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground"
        >
          <HelpCircle className="size-4" />
          What is tracked?
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>What is tracked?</DialogTitle>
          <DialogDescription>
            {tab === 'repositories'
              ? 'Kirin records the git commands your AI coding agents run in a terminal to clone, pull, fetch or push a repository, so you can see which code they accessed.'
              : 'Kirin records the packages your AI coding agents install via terminal commands, so you can see what code was pulled into your environment.'}
          </DialogDescription>
        </DialogHeader>
        {tab === 'repositories' ? (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Tracked git actions</span>
            <ul className="flex flex-col gap-1.5 text-sm">
              {TRACKED_GIT_ACTIONS.map(({ action, command }) => (
                <li key={action} className="grid grid-cols-[96px_1fr] gap-2">
                  <span className="text-muted-foreground">{action}</span>
                  <code>{command}</code>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground">
              The repository is the address in the command. When the command
              names none (for example <code>git fetch</code> or{' '}
              <code>git pull origin main</code>), Kirin uses the git remote of
              the folder the command ran in. A command that first switches to
              another folder (for example <code>cd</code> or <code>git -C</code>
              ), or that runs outside a git repository or in one with no remote,
              is not recorded.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">
              Supported package managers
            </span>
            <ul className="flex flex-col gap-1.5 text-sm">
              {TRACKED_MANAGERS.map(({ ecosystem, managers }) => (
                <li key={ecosystem} className="grid grid-cols-[96px_1fr] gap-2">
                  <span className="text-muted-foreground">{ecosystem}</span>
                  <span>{managers}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

WhatIsTrackedDialog.displayName = 'WhatIsTrackedDialog';
